import { useState, useEffect, useRef } from "react";
import { Formik, Field } from "formik";
import { addQuestion, updateQuestion } from "../../api/Question";
import { getAllLessons } from "../../api/lesson";
import * as Yup from "yup";
import QuestionInlineEditor from "../QuestionInlineEditor";
import { buildSelfContainedHtml } from "../article-editor";

const AddQuestionForm = ({
  setStatus,
  setShowNotification,
  setRefresh,
  edit,
  questionId,
  questionData,
  setEdit,
}) => {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState("");
  const [allLessons, setAllLessons] = useState([]);
  const [selectedBook, setSelectedBook] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [choosedContent, setChoosedContent] = useState(null);
  const [imageUpload, setImageUpload] = useState(false);

  const [initialValues, setInitialValues] = useState({
    questionType: "",
    book: "",
    grade: "",
    difficulty: "",
    lessonId: "",
    content: "",
    aiChat: "",
  });

  // ✅ برای remount کردن ادیتور بعد از ثبت موفق
  const [editorKey, setEditorKey] = useState(0);

  // ✅ برای جلوگیری از ریست شدن فیلدهای انتخابی بعد از ثبت موفق
  const skipResetRef = useRef(false);

  // مقداردهی اولیه / بارگذاری داده در حالت ویرایش
  useEffect(() => {
    if (skipResetRef.current) {
      skipResetRef.current = false;
      return;
    }

    if (edit && questionId && questionData) {
      const item = questionData.find((q) => q._id === questionId);
      if (item) {
        setInitialValues({
          questionType: item.questionType || "",
          book: item.book || "",
          grade: item.grade || "",
          difficulty: item.difficulty || "",
          lessonId: item.lessonId || "",
          content: item.content || "",
          aiChat: "",
        });
        setSelectedGrade(item.grade || "");
        setSelectedBook(item.book || "");
        setChoosedContent(item.content || null);
        setContent(item.content || "");
        setEditorKey((prev) => prev + 1);
      }
    } else if (!edit) {
      setInitialValues({
        questionType: "",
        book: "",
        grade: "",
        difficulty: "",
        lessonId: "",
        content: "",
        aiChat: "",
      });
      setSelectedBook("");
      setSelectedGrade("");
    }
  }, [edit, questionId, questionData]);

  // دریافت دروس
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getAllLessons();
        setAllLessons(response.data.lessons || []);
      } catch (err) {
        console.error("خطا در دریافت دروس:", err);
      }
    };
    fetchData();
  }, [selectedBook, selectedGrade, edit]);

  const validationSchema = Yup.object({
    questionType: Yup.string().required("نوع سوال الزامی است"),
    book: Yup.number().required("انتخاب کتاب الزامی می باشد"),
    grade: Yup.number().required("انتخاب پایه الزامی می باشد"),
    difficulty: Yup.string().required("انتخاب سختی اجباری است"),
    lessonId: Yup.string().required("انتخاب درس اجباری می باشد"),
  });

  const filterLessons = (lesson) => {
    if (lesson.book == selectedBook && lesson.grade == selectedGrade) {
      return (
        <option key={lesson._id} value={lesson._id}>
          {`${lesson.title} -  (فصل ${lesson.season}) درس ${lesson.lesson}`}
        </option>
      );
    }
    return null;
  };

  const handleQuestionForm = async (values) => {
    try {
      setLoading(true);

      if (imageUpload === "uploading") {
        alert("عکس در حال اپلود شدن است");
      }

      if (imageUpload === "uploadErr") {
        alert("در صورت ثبت دوباره همین سوال ، بدون عکس ثبت خواهد شد");
        setImageUpload(false);
      }

      const finalResult = await buildSelfContainedHtml(content);

      const formData = {
        questionType: values.questionType,
        book: selectedBook,
        grade: selectedGrade,
        difficulty: values.difficulty,
        lessonId: values.lessonId,
        content: finalResult,
        aiChat: values.aiChat,
      };

      if (formData.lessonId === "") {
        setStatus(508);
        return;
      }

      let res;

      if (edit && questionId) {
        res = await updateQuestion(formData, questionId);
      } else {
        res = await addQuestion(formData);
      }

      setStatus(res.status);

      // ✅ بعد از ثبت موفق سوال جدید:
      // فقط متن ادیتور پاک شود، باقی فیلدها ثابت بمانند
      if (!edit && res.status === 201) {
        skipResetRef.current = true; // جلوگیری از ریست شدن در useEffect
        setContent("");
        setChoosedContent(null);
        setEditorKey((prev) => prev + 1); // remount ادیتور
      }

      setRefresh((prev) => !prev);
    } catch (err) {
      console.error(err);
      setStatus(502);
    } finally {
      setLoading(false);
      setShowNotification(true);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleQuestionForm}
    >
      {({ values, errors, touched, handleSubmit, handleChange }) => (
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg w-full p-4 md:p-8 space-y-4 md:space-y-6"
        >
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
            {edit ? "ویرایش سوال" : "ایجاد سوال جدید"}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* نوع سوال */}
            <div>
              <label
                htmlFor="questionType"
                className="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2"
              >
                نوع سوال
              </label>
              <Field
                as="select"
                id="questionType"
                name="questionType"
                className="w-full px-3 py-2 md:px-4 md:py-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">انتخاب کنید</option>
                <option value="multipleChoice">چندگزینه‌ای</option>
                <option value="descriptive">تشریحی</option>
                <option value="truefalse">صحیح/غلط</option>
                <option value="shortAnswer">پاسخ کوتاه</option>
                <option value="adjustable">خلاقانه</option>
                <option value="blank">جای خالی</option>
              </Field>
              {errors.questionType && touched.questionType && (
                <div className="text-red-500 text-xs md:text-sm mt-1">
                  {errors.questionType}
                </div>
              )}
            </div>

            {/* پایه تحصیلی */}
            <div>
              <label
                htmlFor="grade"
                className="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2"
              >
                پایه تحصیلی
              </label>
              <Field
                as="select"
                id="grade"
                name="grade"
                className="w-full px-3 py-2 md:px-4 md:py-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                onChange={(e) => {
                  setSelectedGrade(e.target.value);
                  handleChange(e);
                }}
              >
                <option value="">انتخاب کنید</option>
                <option value="1">اول</option>
                <option value="2">دوم</option>
                <option value="3">سوم</option>
                <option value="4">چهارم</option>
                <option value="5">پنجم</option>
                <option value="6">ششم</option>
              </Field>
              {errors.grade && touched.grade && (
                <div className="text-red-500 text-xs md:text-sm mt-1">
                  {errors.grade}
                </div>
              )}
            </div>

            {/* کتاب */}
            <div>
              <label
                htmlFor="book"
                className="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2"
              >
                کتاب
              </label>
              <Field
                as="select"
                id="book"
                name="book"
                className="w-full px-3 py-2 md:px-4 md:py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => {
                  setSelectedBook(e.target.value);
                  handleChange(e);
                }}
              >
                <option value="">انتخاب کنید</option>
                <option value="1">ریاضی</option>
                <option value="2">فارسی و نگارش</option>
                <option value="3">علوم</option>
                <option value="4">قرآن</option>
                <option value="5">املا</option>
                <option value="6">هدیه های آسمانی</option>
                <option value="7">مطالعات اجتماعی</option>
              </Field>
              {errors.book && touched.book && (
                <div className="text-red-500 text-xs md:text-sm mt-1">
                  {errors.book}
                </div>
              )}
            </div>

            {/* درجه سختی */}
            <div>
              <label
                htmlFor="difficulty"
                className="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2"
              >
                درجه سختی
              </label>
              <div className="flex items-center gap-2 md:gap-4">
                {["آسان", "متوسط", "دشوار"].map((difficulty) => (
                  <div key={difficulty} className="flex items-center">
                    <Field
                      type="radio"
                      id={difficulty}
                      name="difficulty"
                      value={difficulty}
                      className="w-3 h-3 md:w-4 md:h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <label
                      htmlFor={difficulty}
                      className="mr-1 md:mr-2 text-xs md:text-sm text-gray-700"
                    >
                      {difficulty}
                    </label>
                  </div>
                ))}
              </div>
              {errors.difficulty && touched.difficulty && (
                <div className="text-red-500 text-xs md:text-sm mt-1">
                  {errors.difficulty}
                </div>
              )}
            </div>

            {/* درس مرتبط */}
            <div>
              <label
                htmlFor="lessonId"
                className="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2"
              >
                درس مرتبط
              </label>
              <Field
                as="select"
                id="lessonId"
                name="lessonId"
                className="w-full px-3 py-2 md:px-4 md:py-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">انتخاب درس</option>
                {allLessons &&
                  [...allLessons]
                    .sort((a, b) => {
                      if (a.season !== b.season) {
                        return a.season - b.season;
                      }
                      return a.lesson - b.lesson;
                    })
                    .map((lesson) => filterLessons(lesson))}
              </Field>
              {errors.lessonId && touched.lessonId && (
                <div className="text-red-500 text-xs md:text-sm mt-1">
                  {errors.lessonId}
                </div>
              )}
            </div>
          </div>

          {/* ✅ ادیتور با key مخصوص remount */}
          <QuestionInlineEditor
            key={`question-editor-${editorKey}`}
            choosedContent={choosedContent}
            content={content}
            setContent={setContent}
          />

          {/* دکمه ثبت */}
          <div className="flex justify-between pt-4 md:pt-6">
            <button
              type="submit"
              disabled={loading}
              className="w-full md:w-auto px-4 py-2 md:px-6 md:py-3 text-sm md:text-base bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50"
            >
              {loading ? "در حال ثبت..." : "ثبت سوال"}
            </button>
            {edit ? (
              <button
                type="reset"
                className="hover:text-red-700 text-red-500"
                onClick={() => setEdit(false)}
              >
                برگشت به ثبت سوال جدید
              </button>
            ) : null}
          </div>
        </form>
      )}
    </Formik>
  );
};

export default AddQuestionForm;
